import {InputField, TwoFactorType} from '@enter/core';
import {Formik, FormikHelpers as FormikActions, FormikProps} from 'formik';
import _ from 'lodash';
import {inject, observer} from 'mobx-react';
import React, {useState} from 'react';
import * as Yup from 'yup';
import * as S from './change-two-factor-dialog.styles';
import {InvalidErrorMessage, QRCodeImage} from "./change-two-factor-dialog.styles";

interface ITwoFactorChallenge {
    code: string;
}

const schema = Yup.object().shape({
    code: Yup.string().length(6, 'Invalid verification code').required('Verification code is required'),
});

const initial: ITwoFactorChallenge = {
    code: '',
};

interface IChangeTwoFactorDialogProps {
    twoFactorType: TwoFactorType
    onClose: () => void;
}

type DialogSteps = 'initiate' | 'verify' | 'error';

const maxLength = 170;

export const ChangeTwoFactorDialog: React.FunctionComponent<IChangeTwoFactorDialogProps> = inject('rootStore')(
    observer(
        (props: IChangeTwoFactorDialogProps): React.ReactElement => {
            const rootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { onClose, twoFactorType } = props;
            const [step, setStep] = useState<DialogSteps>('initiate');
            const [phoneVerificationId, setPhoneVerificationId] = useState<string>('');
            const [qrCodeImage, setQrCodeImage] = useState<string>('');
            const [invalid, setInvalid] = useState<boolean>(false);

            const saveLabel = (): string => {
                switch (step) {
                    case 'error':
                        return 'Try again';
                    case 'initiate':
                        return 'Start';
                    case 'verify':
                    default:
                        return 'Update 2-step verification';
                }
            };

            const startChangeTwoFactor = async (): Promise<void> => {
                try {
                    const initiateResponse = await rootStore.settingsStore.initiateTwoFactorChange(twoFactorType);
                    if (initiateResponse) {
                        if(twoFactorType === TwoFactorType.TOTP) {
                            setQrCodeImage(initiateResponse.qrCodeImage);
                        }
                        setPhoneVerificationId(initiateResponse.phoneVerificationId);
                        setStep('verify');
                    } else {
                        setStep('error');
                    }
                } catch (err) {
                    setStep('error');
                }
            };
            const handleCancel = (): void => {
                switch (step) {
                    case 'error':
                    case 'initiate':
                        onClose();
                        break;
                    case 'verify':
                    default:
                        setStep('initiate');
                }
            };
            const handleError = (): void => {
                setInvalid(true);
            };
            const handleSave = (formik: FormikProps<ITwoFactorChallenge>): (() => Promise<void>) => {
                return async (): Promise<void> => {
                    if (!formik.isValid) {
                        return;
                    }
                    switch (step) {
                        case 'initiate':
                            await startChangeTwoFactor();
                            break;
                        case 'verify':
                            await formik.submitForm();
                            await rootStore.loadUser(); // NB: update rootStore.userTwoFactor
                            onClose();
                            break;
                        case 'error':
                        default:
                            setStep('initiate');
                    }
                };
            };

            const onSubmit = async (
                values: ITwoFactorChallenge,
                { setSubmitting }: FormikActions<any>,
            ): Promise<void> => {
                setSubmitting(true);
                setInvalid(false);
                const { code } = values;
                await rootStore.settingsStore.verifyTwoFactorChange(phoneVerificationId, code);
                setStep('verify');
                setSubmitting(false);
            };

            return (
                <Formik initialValues={initial} validationSchema={schema} onSubmit={onSubmit}>
                    {(formik: FormikProps<ITwoFactorChallenge>): React.ReactElement => (
                        <S.DialogBody>
                            {step === 'initiate' && (
                                <>
                                    <S.InputGroup>
                                        <div>You are about to change your 2-factor preference to <S.TwoFactorChoice>{twoFactorType}</S.TwoFactorChoice>.</div>
                                        <div>To start this process, press the 'Start' button.</div>
                                    </S.InputGroup>
                                </>
                            )}

                            {step === 'verify' && (
                                <>

                                    <S.InputGroup>
                                        {twoFactorType === TwoFactorType.SMS && <span>A 6-digit code has been sent to your phone number on file. Please enter the code below.</span>}
                                        {twoFactorType === TwoFactorType.TOTP && <span>Scan the QR code below using your TOTP app (such as Google Authenticator or Authy). Then enter the code that is displayed (no spaces).</span>}
                                        {twoFactorType === TwoFactorType.TOTP && <S.QRCodeImage alt='QR Code image' src={qrCodeImage}/>}
                                        <S.Label>Verification Code</S.Label>
                                        <S.FormField
                                            name="code"
                                            maxLength={maxLength}
                                            component={InputField}
                                            placeholder="6-digit code"
                                        />
                                        {invalid && <S.InvalidErrorMessage>Unable to verify the code. Please make sure it was entered correctly (without spaces) and try again.</S.InvalidErrorMessage>}
                                    </S.InputGroup>
                                </>
                            )}

                            {step === 'error' && (
                                <>
                                    <S.InputGroup>
                                        Due to a technical difficulty, we could not complete this operation at this
                                        time.
                                    </S.InputGroup>
                                </>
                            )}

                            <S.ButtonGroup
                                changed={true}
                                useNotify={false}
                                onCancel={handleCancel}
                                onError={handleError}
                                onSave={handleSave(formik)}
                                saveLabel={saveLabel()}
                            />
                        </S.DialogBody>
                    )}
                </Formik>
            );
        },
    ),
);
